export default {
  nav: {
    protocol: 'Protocol',
    technology: 'Technology',
    ecosystem: 'Ecosystem',
    developers: 'Developers',
    community: 'Community',
    home: 'Home'
  },
  hero: {
    title: 'Building the Privacy Foundation for Web3',
    subtitle: 'Enable secure, verifiable data sharing through zero-knowledge proofs and homomorphic encryption, achieving true data sovereignty',
    announcement: 'Mainnet Launching Soon · Join Testnet',
    join: {
      testnet: 'Join Testnet'
    },
    cta: {
      build: 'Start Building',
      learn: 'Learn More'
    }
  },
  feature: {
    zkproof: {
      title: 'Zero-Knowledge Proof',
      desc: 'Prove data authenticity without revealing sensitive information'
    },
    homomorphic: {
      title: 'Homomorphic Encryption',
      desc: 'Perform computations directly on encrypted data'
    },
    sovereignty: {
      title: 'Data Sovereignty',
      desc: 'Users maintain complete control over their data and identity'
    },
    crosschain: {
      title: 'Cross-Chain Interoperability',
      desc: 'Seamless integration with mainstream blockchain networks'
    }
  },
  home: {
    coreProblem: 'Core Problem',
    problemTitle: 'Challenges Facing Current Blockchain',
    problemDescription: 'Transparent blockchain technology exposes user data and transaction privacy, causing users to lose control over their data and preventing data value monetization',
    privacyLeakage: {
      title: 'Privacy Leakage',
      description: 'Blockchain\'s transparent nature exposes all transactions and data publicly, revealing users\' financial status, transaction patterns, and other private information.',
      items: ['Transaction amounts and addresses are public', 'User behavior patterns are traceable', 'Business secrets cannot be protected']
    },
    dataOutOfControl: {
      title: 'Data Out of Control',
      description: 'Users have lost control over their data, cannot determine how it\'s used, and cannot benefit from its value.',
      items: ['Data monopolized by platforms', 'Unable to control data usage', 'Data value cannot be monetized']
    },
    techArchitecture: 'Technical Architecture',
    techLayersTitle: 'Four Technical Layers',
    techLayersDescription: 'Building a complete privacy protection technology stack',
    techLayers: {
      privacyLayer: {
        title: '🧠 Privacy Protection Layer',
        description: 'Using zero-knowledge proofs and homomorphic encryption technology to ensure data confidentiality during computation and verification, achieving "verifiable but invisible data".'
      },
      identityLayer: {
        title: '🔐 Data Identity Layer',
        description: 'Based on decentralized identity, allowing users to autonomously manage their identity information and data authorization, possessing complete digital sovereignty.'
      },
      economyLayer: {
        title: '⚙️ Smart Economy Layer',
        description: 'Through privacy-protected smart contracts, building a decentralized data trading market, enabling secure and fair flow of data value.'
      },
      interopLayer: {
        title: '🌐 Interoperability Layer',
        description: 'Achieving cross-chain compatibility with mainstream public chains like Ethereum, BSC, Solana, extending privacy protection capabilities to the entire multi-chain ecosystem.'
      }
    },
    applications: {
      title: 'Application Scenarios',
      subtitle: 'New Applications with Privacy Protection',
      privacyDefi: {
        title: 'Privacy-Protected DeFi',
        description: 'Providing decentralized financial services while protecting user privacy, preventing transaction information leakage.',
        button: 'Learn More'
      },
      dataMarketplace: {
        title: 'Data Marketplace',
        description: 'Building a secure data trading market, allowing data providers to earn revenue without exposing raw data.',
        button: 'Learn More'
      },
      identityVerification: {
        title: 'Identity Verification',
        description: 'Providing decentralized identity verification services, allowing users to prove identity without revealing personal information.',
        button: 'Learn More'
      },
      supplyChainPrivacy: {
        title: 'Supply Chain Privacy',
        description: 'Protecting business secrets in supply chain management while ensuring data verifiability and transparency.',
        button: 'Learn More'
      }
    },
    networkActivity: {
      title: 'Network Activity',
      subtitle: 'Privacy Protection Ecosystem',
      description: 'All activities on the Titron network',
      stats: {
        transactions: {
          value: '10M+',
          description: 'Number of privacy-protected transactions processed'
        },
        dataValue: {
          value: 'US$5B',
          description: 'Data value flowing through the protocol'
        },
        successRate: {
          value: '99.9%',
          description: 'Privacy protection success rate'
        },
        networks: {
          value: '15+',
          description: 'Number of supported blockchain networks'
        }
      },
      buttons: {
        developerEcosystem: 'Developer Ecosystem',
        networkStats: 'Network Statistics'
      }
    },
    valueComparison: {
      title: 'Value Comparison',
      subtitle: 'Web3 is Undergoing Transformation',
      description: 'From data monopoly to data sovereignty',
      traditionalMode: 'Traditional Mode',
      titronValues: ['Data Sovereignty', 'Privacy Protection', 'Verifiable Computing', 'Decentralization', 'Cross-Chain Interoperability', 'Zero-Knowledge Proof', 'User Control'],
      traditionalValues: ['Data Monopoly', 'Privacy Leakage', 'Black Box Computing', 'Centralization', 'Isolated Systems', 'Complete Transparency', 'Platform Control']
    },
    learning: {
      title: 'Learning',
      subtitle: 'Understanding Privacy Protection Technology',
      description: 'Privacy protection technology might seem complex to you. Don\'t worry, these materials will help you understand Titron\'s core concepts in just a few minutes.',
      hotTopics: 'Hot Topics',
      topics: {
        zkProof: 'What is Zero-Knowledge Proof?',
        dataPrivacy: 'How to Protect Data Privacy?',
        dataSovereignty: 'Data Sovereignty Beginner\'s Guide',
        whitepaper: 'Titron Whitepaper'
      },
      moreResources: 'More Learning Resources'
    },
    developers: {
      title: 'Developers',
      subtitle: 'Privacy Protection Developer Community',
      description: 'Titron is the most active developer ecosystem in Web3 privacy protection. Use our SDK and tools to easily build privacy protection applications.',
      buttons: {
        documentation: 'Developer Documentation',
        sdkDownload: 'SDK Download'
      },
      codeExamples: 'Code Examples',
      examples: {
        voting: {
          title: 'Privacy-Protected Voting',
          description: 'Building anonymous voting systems'
        },
        identity: {
          title: 'Zero-Knowledge Identity Verification',
          description: 'Verify identity without revealing information'
        },
        transactions: {
          title: 'Encrypted Data Transactions',
          description: 'Secure data marketplace transactions'
        }
      }
    },
    community: {
      title: 'TITRON COMMUNITY',
      subtitle: 'Built by the Community',
      description1: 'Titron is built by global privacy protection technology enthusiasts, developers, researchers and users, dedicated to advancing Web3 privacy protection technology.',
      description2: 'Welcome to join our community, exchange with privacy protection technology experts worldwide, participate in protocol development, and build a more private and secure Web3 future together.',
      description3: 'The Titron community is the best starting point for learning privacy protection technology and participating in the Web3 privacy revolution.',
      buttons: {
        aboutTitron: 'About Titron',
        discord: 'Discord',
        github: 'GitHub'
      }
    },
    joinTitron: {
      title: 'Join Titron',
      subtitle: 'Participate in building the future of Web3 privacy protection, returning data sovereignty to users',
      contributions: {
        developer: {
          title: 'Developer Contributions',
          description: 'Participate in protocol development, building privacy protection tools and applications.'
        },
        github: {
          title: 'GitHub',
          description: 'View source code, submit issues and improvement suggestions'
        },
        discord: {
          title: 'Discord',
          description: 'Join community discussions, get technical support and latest updates.'
        },
        research: {
          title: 'Research Collaboration',
          description: 'Participate in privacy protection technology research, advancing academic development.'
        }
      }
    }
  },
  common: {
    learnMore: 'Learn More'
  },
  search: {
    placeholder: 'Search'
  },
  language: {
    zh: '简体中文',
    en: 'English'
  },
  footer: {
    protocol: 'Protocol',
    whitepaper: 'Whitepaper',
    roadmap: 'Roadmap',
    governance: 'Governance',
    developers: 'Developers',
    docs: 'Documentation',
    sdk: 'SDK',
    api: 'API',
    community: 'Community',
    discord: 'Discord',
    github: 'GitHub',
    twitter: 'Twitter',
    company: 'Company',
    about: 'About Us',
    careers: 'Join Us',
    contact: 'Contact Us',
    newsletter: {
      title: 'Subscribe to Our Newsletter',
      description: 'Get the latest protocol updates and technical developments',
      placeholder: 'Enter your email',
      subscribe: 'Subscribe'
    },
    copyright: '© 2020-2025 Titron. All rights reserved.',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    cookie: 'Cookie Settings'
  },
  cookieConsent: {
    title: 'Cookie Settings',
    description: 'We use cookies to enhance your browsing experience, provide personalized content, and analyze site traffic. By clicking "Accept All", you consent to our use of all cookies, or customize your preferences.',
    knownBrowser: '✓ Detected known browser, but need to reconfirm cookie preferences',
    newBrowser: 'ℹ First visit to this browser, please set cookie preferences',
    learnMore: 'Learn more about how we use cookies',
    rejectAll: 'Reject All',
    preferences: 'Preferences',
    acceptAll: 'Accept All',
    preferencesTitle: 'Cookie Preferences',
    preferencesDescription: 'We respect your privacy choices. You can accept or reject different types of cookies. Please note that disabling certain cookies may affect website functionality.',
    required: 'Required',
    savePreferences: 'Save Preferences',
    categories: {
      necessary: {
        title: 'Necessary Cookies',
        description: 'These cookies are essential for the basic functions of the website and cannot be disabled. They include security, authentication, and basic functionality cookies.'
      },
      functional: {
        title: 'Functional Cookies',
        description: 'These cookies enhance website functionality, such as remembering your preferences, language choices, and personalized content.'
      },
      analytics: {
        title: 'Analytics Cookies',
        description: 'These cookies help us understand how the website is used, improve user experience and website performance.'
      },
      marketing: {
        title: 'Marketing Cookies',
        description: 'These cookies are used to deliver relevant advertising and marketing content, track ad performance and user engagement.'
      }
    }
  },
  technology: {
    title: 'Titron Technology Stack',
    subtitle: 'Building next-generation privacy protection blockchain infrastructure based on advanced cryptographic technology and innovative consensus mechanisms',
    coreTech: 'Core Technology',
    consensus: 'Consensus Mechanism',
    specs: 'Technical Specifications',
    innovation: 'Innovation Highlights'
  },
  ecosystem: {
    title: 'Titron Ecosystem',
    subtitle: 'Building a diversified privacy protection application ecosystem covering DeFi, privacy computing, digital identity and data marketplace',
    overview: 'Ecosystem Overview',
    categories: 'Ecosystem Categories',
    partnerships: 'Strategic Partnerships',
    upcoming: 'Coming Soon'
  },
  developers: {
    title: 'Titron Developers',
    subtitle: 'Use powerful development tools and rich resources to build next-generation privacy protection applications',
    community: 'Developer Community',
    tools: 'Development Tools',
    quickStart: 'Quick Start',
    examples: 'Code Examples',
    resources: 'Development Resources'
  },
  community: {
    title: 'Titron Community',
    subtitle: 'Join the world\'s largest privacy protection technology community, building the future of Web3 with like-minded developers, researchers and users',
    overview: 'Community Overview',
    channels: 'Community Channels',
    events: 'Community Events',
    governance: 'Governance Structure',
    spotlight: 'Community Highlights'
  }
};
